// File: src/App.js
// Main application component

import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import LoginView from './components/LoginView';
import AdminView from './components/AdminView';
import TabbedStandingsView from './components/TabbedStandingsView';
import ClassTabs from './components/ClassTabs';
import UserManagement from './components/UserManagement';
import PasswordChange from './components/PasswordChange';
import PublicHeader from './components/PublicHeader';
import CalendarView from './components/CalendarView';
import EventDetailView from './components/EventDetailView';
import EntryManageView from './components/EntryManageView';
import MyAccountView from './components/MyAccountView';
import { ForgotPasswordView, ResetPasswordView } from './components/PasswordResetView';
import AppProviders from './components/AppProviders';
import { useLanguage } from './contexts/LanguageContext';
import { useCountry } from './contexts/CountryContext';
import { API_BASE_URL } from './config';
import { formatSeriesNameWithYear, isSeriesActiveInYear, getAvailableYears, getCurrentYear } from './utils/utils';
import { registerSessionExpiredHandler, resetSessionExpiredNotice, handleUnauthorized } from './utils/session';
import { STAFF_ROLES, normalizeRole } from './utils/roles';

// Re-exported for the components that still import it from here. New code
// should import from './config' directly.
export { API_BASE_URL };

// Fetch with retry logic and exponential backoff
async function fetchWithRetry(url, options = {}, maxRetries = 3) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // If request succeeded, return response
      if (response.ok || response.status === 401 || response.status === 403) {
        return response;
      }

      // For server errors (5xx), retry
      if (response.status >= 500 && attempt < maxRetries) {
        lastError = new Error(`Server error: ${response.status}`);
        const delay = Math.min(1000 * Math.pow(2, attempt), 8000); // Exponential backoff, max 8s
        console.warn(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      return response;
    } catch (error) {
      lastError = error;

      // Don't retry on abort or client errors
      if (error.name === 'AbortError') {
        console.error('Request timeout');
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
          console.warn(`Request timeout (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      } else if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
        console.warn(`Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
}

function PublicView({ series, selectedSeries, setSelectedSeries, selectedClass, setSelectedClass }) {
  const { t } = useLanguage();
  const { currentCountry } = useCountry();
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());

  // Filter series by selected year and country
  const filteredSeries = series.filter(s =>
    isSeriesActiveInYear(s, selectedYear) && s.country === currentCountry.code
  );
  const availableYears = getAvailableYears(series);

  // Auto-select first series in filtered list if current selection is not visible
  useEffect(() => {
    if (filteredSeries.length > 0) {
      const isCurrentSeriesVisible = filteredSeries.some(s => s.id === selectedSeries);
      if (!isCurrentSeriesVisible) {
        setSelectedSeries(filteredSeries[0].id);
      }
    }
  }, [selectedYear, filteredSeries, selectedSeries, setSelectedSeries]);

  const currentSeries = filteredSeries.find(s => s.id === selectedSeries);
  const classes = currentSeries?.classes || [];

  // Fall back to the first class rather than showing nothing while the
  // selection catches up with a series that has just changed underneath it.
  const currentClass = classes.find(c => c.id === selectedClass) || classes[0];

  return (
    <div className="app-shell p-4">
      <div className="max-w-6xl mx-auto">
        <PublicHeader />

        {series.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-ink-muted mb-2 font-semibold">{t('selectYear')}:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-ink-muted mb-2 font-semibold">{t('selectSeries')}:</label>
                <select
                  value={selectedSeries || ''}
                  onChange={(e) => setSelectedSeries(parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
                  disabled={filteredSeries.length === 0}
                >
                  {filteredSeries.map(s => (
                    <option key={s.id} value={s.id}>
                      {formatSeriesNameWithYear(s.name, s.start_year, s.end_year)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {filteredSeries.length === 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded mb-6">
                {t('noSeriesForYear')} {selectedYear}
              </div>
            )}

            <ClassTabs
              classes={classes}
              selectedClassId={currentClass?.id}
              onSelect={setSelectedClass}
            />

            {currentClass && <TabbedStandingsView seriesClass={currentClass} isPublic={true} />}
          </>
        ) : (
          <div className="bg-paper rounded-lg p-12 text-center border border-line">
            <Trophy className="w-16 h-16 text-ink-muted mx-auto mb-4" />
            <p className="text-ink-muted text-lg">{t('noSeriesAvailable')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// The existing state-driven application: public standings, login, and the
// admin area. `initialView` is how a route reaches into it - /login opens the
// sign-in form - without rewriting the state machine into routes.
function MainApp({ initialView = 'public' }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [view, setView] = useState(initialView);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || 'competitor');
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [series, setSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  // Which class of that series is on screen. Cleared whenever the series
  // changes, so the views fall back to its first class rather than holding a
  // selection that belongs to a championship nobody is looking at.
  const [selectedClass, setSelectedClass] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // On /login with nobody signed in, the sign-in form is what belongs there -
  // whatever the view state currently says.
  //
  // This is an invariant, not a convenience. The view is state while the page
  // is a route, and anything that set the view back to 'public' while the URL
  // was /login - logging out, a session expiring, a stale token failing on
  // load - produced a page whose Login button linked to the URL it was already
  // on. React Router treats that as no navigation, so the button did nothing
  // and the only way back in was to edit the address bar.
  useEffect(() => {
    if (pathname === '/login' && !token) setView('login');
  }, [pathname, token]);

  useEffect(() => {
    // Any authenticated request that hits a 401 will route through here to
    // notify the user and drop them back to the login screen.
    registerSessionExpiredHandler(handleLogout);
    loadPublicSeries();
    if (token) {
      setView('admin');
      verifySession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The role in localStorage is user-editable and can be stale after an admin
  // changes it, so ask the server who we actually are. The server enforces
  // permissions regardless; this keeps the UI from offering actions that will
  // only fail with a 403.
  const verifySession = async () => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (response.ok) {
        const me = await response.json();
        setUserRole(me.role);
        setUsername(me.username);
        localStorage.setItem('userRole', me.role);
        localStorage.setItem('username', me.username);
      }
    } catch (error) {
      // Offline or the API is down - keep the cached role and let the next
      // authenticated request surface the problem.
      console.error('Could not verify session:', error);
    }
  };

  const loadPublicSeries = async () => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/public/series`);
      if (response.ok) {
        const data = await response.json();
        setSeries(data);
        if (data.length > 0 && !selectedSeries) {
          setSelectedSeries(data[0].id);
        }
      } else {
        console.error('Failed to load series:', response.status);
      }
    } catch (error) {
      console.error('Error loading series after retries:', error);
      // Show user-friendly error message
      alert('Unable to load data. Please check your connection and try again.');
    }
  };

  const loadSeries = async (authToken = null) => {
    try {
      const tokenToUse = authToken || token;
      if (!tokenToUse) {
        console.warn('No token available for loadSeries');
        return;
      }
      const response = await fetchWithRetry(`${API_BASE_URL}/series`, {
        headers: { 'Authorization': `Bearer ${tokenToUse}` }
      });
      if (handleUnauthorized(response)) {
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSeries(data);
        if (data.length > 0 && !selectedSeries) {
          setSelectedSeries(data[0].id);
        }
      } else {
        console.error('Failed to load series:', response.status);
      }
    } catch (error) {
      console.error('Error loading series after retries:', error);
      alert('Unable to load data. Please check your connection and try again.');
    }
  };

  const handleLogin = async (username, password) => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok) {
        resetSessionExpiredNotice();
        setToken(data.token);
        setUserRole(data.user.role || 'competitor');
        setUsername(data.user.username);
        localStorage.setItem('token', data.token);
        localStorage.setItem('userRole', data.user.role || 'competitor');
        localStorage.setItem('username', data.user.username);
        setView('admin');
        // Organizers and competitors get a 403 from /api/series; only load it
        // for the roles that can actually use it.
        if (STAFF_ROLES.includes(normalizeRole(data.user.role))) {
          loadSeries(data.token); // Pass token directly since state hasn't updated yet
        }
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error after retries:', error);
      return { success: false, error: 'Connection error. Please try again.' };
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUserRole('competitor');
    setUsername('');
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    setView('public');
    loadPublicSeries();
    // Back to the front page. Signing out used to leave the URL wherever it
    // was - usually /login, since that is where signing in happens - and a
    // public page sitting at /login is the state described above.
    navigate('/');
  };

  if (showUserManagement) {
    return (
        <UserManagement token={token} userRole={userRole} onBack={() => setShowUserManagement(false)} onLogout={handleLogout} />
    );
  }

  if (showPasswordChange) {
    return (
        <PasswordChange token={token} onBack={() => setShowPasswordChange(false)} onLogout={handleLogout} />
    );
  }

  if (view === 'login') {
    return (
        <LoginView
          onLogin={handleLogin}
          // Back to the front page rather than just flipping the view: leaving
          // the URL on /login would put the sign-in form back on a reload.
          onCancel={() => navigate('/')}
        />
    );
  }

  const currentSeries = series.find(s => s.id === selectedSeries);

  const chooseSeries = (id) => {
    setSelectedSeries(id);
    setSelectedClass(null);
  };

  // A competitor has no admin panel; signing in lands them on their own account.
  if (view === 'admin' && userRole === 'competitor') {
    return (
        <MyAccountView
          token={token}
          username={username}
          handleLogout={handleLogout}
          setShowPasswordChange={setShowPasswordChange}
          onBrowseCalendar={() => { window.location.hash = '#/calendar'; }}
        />
    );
  }

  if (view === 'admin') {
    return (
        <AdminView
          series={series}
          selectedSeries={selectedSeries}
          setSelectedSeries={chooseSeries}
          selectedClass={selectedClass}
          setSelectedClass={setSelectedClass}
          currentSeries={currentSeries}
          token={token}
          userRole={userRole}
          username={username}
          handleLogout={handleLogout}
          setShowUserManagement={setShowUserManagement}
          setShowPasswordChange={setShowPasswordChange}
          loadSeries={loadSeries}
        />
    );
  }

  // Public View
  return (
      <PublicView
        series={series}
        selectedSeries={selectedSeries}
        setSelectedSeries={chooseSeries}
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
      />
  );
}

// Chrome shared by every public page. The standings page builds the same shell
// itself because it needs the year and series pickers between the header and
// its content.
function PublicPageShell({ children }) {
  return (
    <div className="app-shell p-4">
      <div className="max-w-6xl mx-auto">
        <PublicHeader />
        {children}
      </div>
    </div>
  );
}

// A distinct component type, not <MainApp initialView="login" /> inline.
// React reconciles by element type: rendering MainApp at the same position for
// both /standings and /login would reuse the mounted instance, and its
// useState(initialView) would never run again - the URL changed and the login
// form never appeared.
function LoginPage() {
  return <MainApp initialView="login" />;
}

function CalendarPage() {
  const { t } = useLanguage();
  const { currentCountry } = useCountry();

  return (
    <PublicPageShell>
      <h1 className="text-3xl font-bold text-ink mb-6">{t('calendarTitle')}</h1>
      <CalendarView initialCountry={currentCountry.code} />
    </PublicPageShell>
  );
}

function EventPage() {
  return (
    <PublicPageShell>
      <EventDetailView />
    </PublicPageShell>
  );
}

function EntryPage() {
  return (
    <PublicPageShell>
      <EntryManageView />
    </PublicPageShell>
  );
}

function App() {
  return (
    <AppProviders>
      {/* HashRouter, not BrowserRouter: the app deploys as static files with no
          rewrite-to-index rule, so a deep link under BrowserRouter would 404 at
          the web server before React ever loaded. */}
      <HashRouter>
        <Routes>
          {/* The calendar is the front page: what most visitors come for is the
              next race, not last season's points. */}
          <Route path="/" element={<CalendarPage />} />
          {/* Kept so links already sent out - and the calendar's own internal
              links - still land somewhere. */}
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/standings" element={<MainApp />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/event/:eventId" element={<EventPage />} />
          {/* The token in this URL is the entrant's credential; it is
              unguessable rather than secret. */}
          <Route path="/entry/:entryToken" element={<EntryPage />} />
          <Route path="/forgot" element={<PublicPageShell><ForgotPasswordView /></PublicPageShell>} />
          {/* The token here is a short-lived, single-use credential. */}
          <Route path="/reset/:resetToken" element={<PublicPageShell><ResetPasswordView /></PublicPageShell>} />
          <Route path="*" element={<MainApp />} />
        </Routes>
      </HashRouter>
    </AppProviders>
  );
}

export default App;